<script setup>
/**
 * Authoring for the campaign's written content: headline, rich body and up
 * to five CTAs. The image moved to the Design tab. CTA edits are emitted
 * immutably so the page's autosave watcher sees every change — the same
 * state the preview popovers edit, so the two stay in sync by construction.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import CtaEditor from './CtaEditor.vue';
import {
  MAX_CTAS,
  applyCtaPatch,
  createCta,
} from 'dashboard/components-next/Campaigns/helpers/campaignContent';

const headline = defineModel('headline', { type: String, default: '' });
const body = defineModel('body', { type: String, default: '' });
const ctas = defineModel('ctas', { type: Array, default: () => [] });

const { t } = useI18n();

const canAddCta = computed(() => ctas.value.length < MAX_CTAS);

const addCta = () => {
  if (!canAddCta.value) return;
  ctas.value = [...ctas.value, createCta()];
};

const removeCta = index => {
  ctas.value = ctas.value.filter((_, i) => i !== index);
};

const updateCta = (index, patch) => {
  ctas.value = ctas.value.map((cta, i) =>
    i === index ? applyCtaPatch(cta, patch) : cta
  );
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <Input
      v-model="headline"
      :label="t('CAMPAIGN.BUILDER.CONTENT.HEADLINE.LABEL')"
      :placeholder="t('CAMPAIGN.BUILDER.CONTENT.HEADLINE.PLACEHOLDER')"
    />

    <Editor
      v-model="body"
      :label="t('CAMPAIGN.BUILDER.CONTENT.BODY.LABEL')"
      :placeholder="t('CAMPAIGN.BUILDER.CONTENT.BODY.PLACEHOLDER')"
      :max-length="500"
    />

    <fieldset class="flex flex-col min-w-0 gap-2">
      <legend class="mb-0.5 text-heading-3 text-n-slate-12">
        {{ t('CAMPAIGN.BUILDER.CONTENT.CTAS.TITLE') }}
      </legend>
      <p class="mb-1 text-xs text-n-slate-11">
        {{ t('CAMPAIGN.BUILDER.CONTENT.CTAS.DESCRIPTION') }}
      </p>

      <div
        v-for="(cta, index) in ctas"
        :key="index"
        class="p-3 border rounded-lg border-n-weak bg-n-alpha-black2"
      >
        <CtaEditor
          :cta="cta"
          @update="updateCta(index, $event)"
          @remove="removeCta(index)"
        />
      </div>

      <Button
        v-if="canAddCta"
        variant="faded"
        color="slate"
        size="sm"
        icon="i-lucide-plus"
        :label="t('CAMPAIGN.BUILDER.CONTENT.CTAS.ADD')"
        class="self-start"
        @click="addCta"
      />
    </fieldset>
  </div>
</template>
